/**
 * 智能路径生成工具
 * 用于根据菜单节点数据生成Vue Router路径
 * 
 * 节点数据结构示例：
 * {
 *   id: 91,
 *   pid: 90,
 *   src: "sxadmin/users/add",
 *   apisrc: "api/user/users/add",
 *   path: null,
 *   title: "新建/编辑",
 *   name: "会员",
 *   icon: "",
 *   menu: 2,
 *   sort: 0,
 *   status: 1
 * }
 */

// 中文到英文的简单映射表，用于路径生成
const chineseToEnglishMap = {
  '新建': 'create',
  '编辑': 'edit',
  '删除': 'delete',
  '查看': 'view',
  '添加': 'add',
  '修改': 'modify',
  '设置': 'settings',
  '管理': 'manage',
  '系统': 'system',
  '用户': 'user',
  '客户': 'client',
  '案件': 'case',
  '案源': 'case-source',
  '财务': 'finance',
  '人事': 'personnel',
  '综合': 'synthesis',
  '房地产': 'realty',
  '自有': 'self-owned',
  '权限': 'permission',
  '部门': 'department',
  '行业': 'industry',
  '岗位': 'position',
  '职称': 'title',
  '审批': 'approval',
  '流程': 'process',
  '功能': 'function',
  '节点': 'node',
  '操作': 'operation',
  '日志': 'log',
  '列表': 'list',
  '详情': 'detail',
  '信息': 'info',
  '配置': 'config',
  '上传': 'upload',
  '下载': 'download',
  '导出': 'export',
  '导入': 'import',
  '搜索': 'search',
  '筛选': 'filter',
  '排序': 'sort',
  '重置': 'reset',
  '提交': 'submit',
  '保存': 'save',
  '取消': 'cancel',
  '确认': 'confirm',
  '返回': 'back'
};

/**
 * 生成路由路径
 * @param {Object} node - 菜单节点对象
 * @param {Object} parentNode - 父节点对象（可选）
 * @param {Object} options - 配置选项（可选）
 * @returns {string} 生成的路由路径
 */
export function generateRoutePath(node, parentNode = null, options = {}) {
  const defaultOptions = {
    useChineseMapping: true,  // 是否使用中文映射
    preferSrcOverTitle: true, // 是否优先使用src字段
    keepOriginalPath: true    // 是否保留原始path字段（如果存在）
  };
  
  const config = { ...defaultOptions, ...options };
  
  // 1. 如果节点有path字段且不为空，直接使用（根据配置）
  if (config.keepOriginalPath && node.path && node.path.trim() !== '') {
    return normalizePath(node.path);
  }
  
  // 2. 如果有src字段，尝试从src生成路径
  if (config.preferSrcOverTitle && node.src && node.src.trim() !== '') {
    const pathFromSrc = convertSrcToPath(node.src);
    if (pathFromSrc && pathFromSrc !== '/') {
      return pathFromSrc;
    }
  }
  
  // 3. 如果有父节点，结合父节点路径生成
  if (parentNode && parentNode.path) {
    const parentPath = normalizePath(parentNode.path);
    const childPath = generateChildPath(node, config);
    if (childPath) {
      return `${parentPath}/${childPath}`.replace(/\/\/+/g, '/');
    }
  }
  
  // 4. 使用title或name生成独立路径
  if (node.title || node.name) {
    const pathFromTitle = convertTitleToPath(node.title || node.name, config);
    if (pathFromTitle && pathFromTitle !== '/') {
      return pathFromTitle;
    }
  }
  
  // 5. 最后手段：使用ID生成路径
  return `/node/${node.id}`;
}

/**
 * 生成子路径
 * @param {Object} node - 子节点对象
 * @param {Object} config - 配置选项
 * @returns {string} 子路径
 */
function generateChildPath(node, config) {
  // 优先使用src字段
  if (node.src && node.src.trim() !== '') {
    const path = convertSrcToPath(node.src);
    // 提取最后一部分作为子路径
    const parts = path.split('/').filter(p => p);
    if (parts.length > 0) {
      return parts[parts.length - 1];
    }
  }
  
  // 使用title或name
  if (node.title || node.name) {
    return convertTitleToPath(node.title || node.name, config, true);
  }
  
  // 使用ID
  return String(node.id);
}

/**
 * 将src字段转换为路径
 * @param {string} src - src字段值
 * @returns {string} 转换后的路径
 */
function convertSrcToPath(src) {
  if (!src || typeof src !== 'string') {
    return '';
  }
  
  let path = src.trim();
  
  // 常见的前缀处理
  const prefixes = ['sxadmin/', 'admin/', 'api/', 'sxadmin_', 'admin_'];
  prefixes.forEach(prefix => {
    if (path.startsWith(prefix)) {
      path = path.substring(prefix.length);
    }
  });
  
  // 替换下划线、点和斜杠为路径分隔符
  path = path.replace(/[._]/g, '/');
  
  // 移除重复的斜杠
  path = path.replace(/\/\/+/g, '/');
  
  // 确保以/开头
  if (!path.startsWith('/')) {
    path = '/' + path;
  }
  
  // 移除末尾的斜杠（除非是根路径）
  if (path !== '/' && path.endsWith('/')) {
    path = path.slice(0, -1);
  }
  
  return path;
}

/**
 * 将标题转换为路径
 * @param {string} title - 标题文本
 * @param {Object} config - 配置选项
 * @param {boolean} forChildPath - 是否用于子路径（不添加斜杠）
 * @returns {string} 转换后的路径
 */
function convertTitleToPath(title, config, forChildPath = false) {
  if (!title || typeof title !== 'string') {
    return forChildPath ? '' : '/';
  }
  
  let result = title.trim();
  
  // 移除特殊字符
  result = result.replace(/[\/\\]/g, '-');  // 替换斜杠
  result = result.replace(/[（）()【】\[\]{}]/g, ''); // 移除括号和方括号
  result = result.replace(/[！!？?，,。.；;：:""'']/g, ''); // 移除标点
  
  // 中文到英文映射
  if (config.useChineseMapping) {
    Object.entries(chineseToEnglishMap).forEach(([ch, en]) => {
      result = result.replace(new RegExp(ch, 'g'), en);
    });
  }
  
  // 处理剩余的中文字符（转换为拼音首字母或简单编码）
  result = result.replace(/[\u4e00-\u9fa5]/g, char => {
    // 简单实现：转换为字符的Unicode编码的简写
    return 'c' + char.charCodeAt(0).toString(16).slice(-2);
  });
  
  // 将空格和多个连字符替换为单个连字符
  result = result.replace(/\s+/g, '-');
  result = result.replace(/-+/g, '-');
  
  // 转换为小写
  result = result.toLowerCase();
  
  // 移除首尾的连字符
  result = result.replace(/^-+/, '').replace(/-+$/, '');
  
  if (!result) {
    return forChildPath ? '' : '/';
  }
  
  return forChildPath ? result : '/' + result;
}

/**
 * 规范化路径
 * @param {string} path - 原始路径
 * @returns {string} 规范化后的路径
 */
export function normalizePath(path) {
  if (!path || typeof path !== 'string') {
    return '/';
  }
  
  let normalized = path.trim();
  
  // 确保以/开头
  if (!normalized.startsWith('/')) {
    normalized = '/' + normalized;
  }
  
  // 移除重复的斜杠
  normalized = normalized.replace(/\/\/+/g, '/');
  
  // 移除末尾的斜杠（除非是根路径）
  if (normalized !== '/' && normalized.endsWith('/')) {
    normalized = normalized.slice(0, -1);
  }
  
  return normalized;
}

/**
 * 处理路径冲突
 * 当多个节点生成相同路径时，添加后缀避免冲突
 * @param {Array} paths - 路径数组
 * @returns {Array} 处理后的路径数组（带有唯一性后缀）
 */
export function resolvePathConflicts(paths) {
  const pathCount = {};
  const resolvedPaths = [];
  
  paths.forEach((path, index) => {
    let resolvedPath = path;
    let count = 1;
    
    // 检查路径是否已存在
    while (pathCount[resolvedPath]) {
      resolvedPath = `${path}-${count}`;
      count++;
    }
    
    pathCount[resolvedPath] = true;
    resolvedPaths.push(resolvedPath);
  });
  
  return resolvedPaths;
}

/**
 * 批量生成路由路径
 * @param {Array} nodes - 节点数组
 * @param {Object} parentMap - 父节点映射表（可选）
 * @param {Object} options - 配置选项
 * @returns {Array} 生成的路径数组
 */
export function generateRoutePaths(nodes, parentMap = {}, options = {}) {
  if (!Array.isArray(nodes) || nodes.length === 0) {
    return [];
  }
  
  // 首先生成所有路径
  const rawPaths = nodes.map(node => {
    const parentNode = parentMap[node.pid];
    return generateRoutePath(node, parentNode, options);
  });
  
  // 处理路径冲突
  return resolvePathConflicts(rawPaths);
}

/**
 * 构建节点映射表
 * @param {Array} nodes - 节点数组
 * @returns {Object} 节点映射表 { id: node }
 */
export function buildNodeMap(nodes) {
  if (!Array.isArray(nodes)) {
    return {};
  }
  
  const nodeMap = {};
  nodes.forEach(node => {
    if (node && node.id !== undefined) {
      nodeMap[node.id] = node;
    }
  });
  
  return nodeMap;
}

/**
 * 构建树形结构
 * @param {Array} nodes - 平铺的节点数组
 * @returns {Array} 树形结构的节点数组
 */
export function buildTree(nodes) {
  if (!Array.isArray(nodes) || nodes.length === 0) {
    return [];
  }
  
  const nodeMap = buildNodeMap(nodes);
  const rootNodes = [];
  
  // 第一遍：复制节点并添加children数组
  Object.values(nodeMap).forEach(node => {
    node.children = [];
  });
  
  // 第二遍：构建树形结构
  nodes.forEach(node => {
    const nodeWithChildren = nodeMap[node.id];
    if (node.pid === 0 || !nodeMap[node.pid]) {
      // 根节点或父节点不存在
      rootNodes.push(nodeWithChildren);
    } else {
      // 添加到父节点的children中
      const parent = nodeMap[node.pid];
      if (parent && parent.children) {
        parent.children.push(nodeWithChildren);
      }
    }
  });
  
  return rootNodes;
}

export default {
  generateRoutePath,
  normalizePath,
  resolvePathConflicts,
  generateRoutePaths,
  buildNodeMap,
  buildTree
};
